<template>
    <PanelFrame ref="panelFrame" panel-class="note-app" resize-directions="all">
        <template #titlebar-left>
            <button class="panel-titlebar-btn" @click.stop="sidebarCollapsed = !sidebarCollapsed" title="Sidebar">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="2" y="2" width="20" height="20" rx="2" ry="2"/><line x1="9" y1="2" x2="9" y2="22"/>
                </svg>
            </button>
            <span class="note-app-titlebar-text">{{ $t('pages.module.NOTE.title') }}</span>
        </template>

        <template #titlebar-actions>
            <button class="panel-titlebar-btn" @click.stop="refreshAll" :title="$t('pages.module.NOTE.refresh')">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>
            </button>
        </template>

        <template #default>
                <div class="note-app-sidebar" v-show="!sidebarCollapsed" :style="isCompact ? {} : {width: sidebarWidth + 'px'}">
                    <NoteSidebar
                        ref="sidebar"
                        :category-tree="categoryTree"
                        :tags="tags"
                        :drafts-count="drafts.length"
                        :trash-count="trashNotes.length"
                        :active-view="activeView"
                        :compact="isCompact"
                        :selected-category-id="selectedCategoryId"
                        :selected-tag-id="selectedTagId"
                        :selected-color-hex="selectedColorHex"
                        :include-shared="includeShared"
                        @selectView="onSelectView"
                        @selectCategory="onSelectCategory"
                        @selectTag="onSelectTag"
                        @selectColor="onSelectColor"
                        @toggleIncludeShared="onToggleIncludeShared"
                        @addCategory="onAddCategory"
                        @addTag="onAddTag"
                        @categoryContextMenu="onCategoryContextMenu"
                        @tagContextMenu="onTagContextMenu"
                        @noteDrop="onNoteDrop"
                        @noteDropTag="onNoteDropTag"
                        @noteDropTrash="onNoteDropTrash"
                        @categoryDropTrash="onCategoryDropTrash"
                        @tagDropTrash="onTagDropTrash"
                        @categoryDrop="onCategoryDrop"
                        @categoryReorder="onCategoryReorder"
                        @renameCategory="onRenameCategory"
                    />
                </div>


                <div class="note-app-divider panel-divider" v-show="!sidebarCollapsed && !isCompact" @mousedown="startSidebarResize" @touchstart.prevent="startSidebarResize"></div>


                <div class="note-app-list" v-show="!isCompact || mobileView === 'list'" :style="isCompact ? {} : (!hasListContent && !selectedNoteId ? {flex: 1, borderRight: 'none'} : {width: listWidth + 'px'})">
                    <NoteTrash v-if="activeView === 'trash'"
                               :notes="trashNotes"
                               :selected-note-id="selectedNoteId"
                               @selectNote="onTrashSelectNote"
                               @restore="onTrashRestore"
                               @permanentDelete="onTrashPermanentDelete"/>
                    <NoteList v-else
                              ref="noteList"
                              :notes="filteredNotes"
                              :selected-note-id="selectedNoteId"
                              :selected-note-ids="selectedNoteIds"
                              :active-view="activeView"
                              :compact="isCompact"
                              @selectNote="onSelectNote"
                              @multiSelectNote="onMultiSelectNote"
                              @newNote="onNewNote"
                              @search="onSearch"
                              @noteContextMenu="onNoteContextMenu"
                              @swipePin="onTogglePin"
                              @swipeDelete="onDeleteNote"
                              @touchDragCategory="onNoteDrop"
                              @touchDragTrash="onTouchDragTrash"/>
                </div>


                <div class="note-app-divider panel-divider" v-show="!isCompact && (hasListContent || selectedNoteId)" @mousedown="startListResize" @touchstart.prevent="startListResize"></div>


                <div class="note-app-editor" v-show="(!isCompact || mobileView === 'editor') && (hasListContent || selectedNoteId)">
                    <div v-if="isCompact && mobileView === 'editor'" class="note-app-mobile-back">
                        <button class="mobile-back-btn" @click="mobileView = 'list'">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
                            <span>{{ activeView === 'trash' ? $t('pages.module.NOTE.trash.title') : $t('pages.module.NOTE.list.backToList') }}</span>
                        </button>
                        <div v-if="selectedNoteDetail && !selectedNoteDetail._isTrashed" class="mobile-back-actions">
                            <template v-if="!selectedNoteDetail._isTemp">
                                <button class="toolbar-icon-btn" :class="{'toolbar-icon-btn--pinned': selectedNoteDetail.is_pinned}" @click="onTogglePin(selectedNoteDetail)">
                                    <svg width="15" height="15" viewBox="0 0 24 24" :fill="selectedNoteDetail.is_pinned ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2"><path d="M9 4v6l-2 4h5v6l1-1 1 1v-6h5l-2-4V4"/><line x1="8" y1="4" x2="16" y2="4"/></svg>
                                </button>
                                <button class="toolbar-icon-btn" :class="{'toolbar-icon-btn--reminder-active': !!selectedNoteDetail.has_agenda}" @click="$refs.editor && ($refs.editor.showReminderPopover = !$refs.editor.showReminderPopover)">
                                    <span class="reminder-icon-wrapper">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                        <span v-if="selectedNoteDetail.has_agenda" class="reminder-dot"></span>
                                    </span>
                                </button>
                                <button v-if="selectedNoteDetail.is_owner" class="toolbar-icon-btn" :class="{'toolbar-icon-btn--active': $refs.editor && $refs.editor.showSettings}" @click="$refs.editor && ($refs.editor.showSettings = !$refs.editor.showSettings)">
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                                </button>
                            </template>
                            <button v-if="selectedNoteDetail.is_owner" class="toolbar-icon-btn toolbar-icon-btn--delete" @click="onDeleteNote(selectedNoteDetail)">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                            </button>
                        </div>
                    </div>
                    <div v-if="isMultiSelectActive" class="note-multi-select-panel">
                        <div class="note-multi-select-info">
                            <span class="note-multi-select-count">{{ multiSelectCount }}</span>
                            <span class="note-multi-select-label">{{ $t('pages.module.NOTE.list.notesSelected') }}</span>
                        </div>
                        <div class="note-multi-select-actions">
                            <button class="note-multi-select-btn note-multi-select-btn--delete" @click="onBatchDelete">
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                                {{ $t('pages.module.NOTE.editor.delete') }}
                            </button>
                        </div>
                        <div class="note-multi-select-hint">{{ $t('pages.module.NOTE.list.dragToMove') }}</div>
                    </div>
                    <NoteEditor v-else
                        ref="editor"
                        :note="selectedNoteDetail"
                        :loading="noteLoading"
                        :categories="categoryTree"
                        :tags="tags"
                        :officers="officers"
                        :compact="isCompact"
                        :uploading="!!noteUploadProgress"
                        @save="onSaveNote"
                        @delete="onDeleteNote"
                        @pin="onTogglePin"
                        @colorChange="onColorChange"
                        @reminderSave="onReminderSave"
                        @reminderDelete="onReminderDelete"
                        @draftUpdate="onDraftUpdate"/>
                </div>

                <div class="note-upload-overlay" v-if="noteUploadProgress">
                    <div class="note-upload-overlay-content">
                        <div class="note-upload-overlay-spinner"></div>
                        <div class="note-upload-overlay-title">{{ $t('pages.module.NOTE.editor.uploadingImages') }}</div>
                        <div class="note-upload-overlay-stats">
                            <span>{{ $t('pages.module.NOTE.editor.uploadFileCount', {completed: noteUploadProgress.completed, total: noteUploadProgress.total}) }}</span>
                            <span v-if="noteUploadProgress.speed">{{ noteUploadProgress.speed }}</span>
                        </div>
                        <div class="note-upload-overlay-bar">
                            <div class="note-upload-overlay-bar-fill" :style="{width: noteUploadProgress.percent + '%'}"></div>
                        </div>
                        <div class="note-upload-overlay-filename">{{ noteUploadProgress.currentFileName }}</div>
                        <button class="note-upload-overlay-cancel" @click="onCancelUpload">{{ $t('pages.module.NOTE.editor.uploadCancel') }}</button>
                    </div>
                </div>
        </template>

        <template #overlays>
            <NoteContextMenu
                ref="contextMenu"
                :category-tree="categoryTree"
                :compact="isCompact"
                :z-base="panelZBase"
                @pin="onContextPin"
                @move="onContextMove"
                @duplicate="onContextDuplicate"
                @share="onContextShare"
                @delete="onContextDelete"
                @renameCategory="onContextRenameCategory"
                @newSubCategory="onContextNewSubCategory"
                @categoryColor="onContextCategoryColor"
                @deleteCategory="onContextDeleteCategory"
                @deleteTag="onContextDeleteTag"/>
        </template>
    </PanelFrame>
</template>

<script>
import NoteSidebar from './NoteSidebar.vue'
import NoteList from './NoteList.vue'
import NoteEditor from './NoteEditor.vue'
import NoteTrash from './NoteTrash.vue'
import NoteContextMenu from './NoteContextMenu.vue'
import PanelFrame from '../panel/PanelFrame.vue'
import { createPanelMixin } from '../panel/usePanelBase'

function generateTitleFromContent(content) {
    if (!content) return ''
    const text = content.replace(/<\s*(br|\/p|\/div|\/li|\/h[1-6])[^>]*>/gi, '\n')
    const decoded = text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    const stripped = decoded.replace(/\xC2?\xA0/g, ' ')
    const firstLine = stripped.trim().split('\n')[0] || ''
    return firstLine.trim().substring(0, 100)
}

function generatePreview(content, title) {
    if (!content) return null
    const text = content.replace(/<\s*(br|\/p|\/div|\/li|\/h[1-6])[^>]*>/gi, '\n')
    const decoded = text.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    let stripped = decoded.replace(/\xC2?\xA0/g, ' ').trim()
    if (title && stripped.startsWith(title)) {
        stripped = stripped.substring(title.length).trim()
    }
    if (!stripped) return null
    stripped = stripped.replace(/\s+/g, ' ').trim()
    return stripped.substring(0, 160)
}

const DRAFT_STORAGE_KEY = 'noteAppDrafts'
const DRAFT_TRASH_KEY = 'noteAppDraftTrash'
const MIN_LIST = 200
const MAX_LIST = 330

const DEFAULTS = {
    floatingMode: false,
    x: null,
    y: null,
    width: 1100,
    height: 700,
    sidebarWidth: 200,
    listWidth: 280,
}

const notePanelMixin = createPanelMixin({
    panelId: 'noteApp',
    shortcut: { code: 'KeyN' },
    minWidth: 600,
    minHeight: 400,
    minSidebar: 150,
    maxSidebar: 280,
    defaults: DEFAULTS,
    dividers: [
        { name: 'list', property: 'listWidth', min: MIN_LIST, max: MAX_LIST },
    ],
    onOpen(vm) {
        if (vm.isCompact) {
            vm.sidebarCollapsed = true
            vm.mobileView = 'list'
        }
        vm.loadData()
    },
    onClose(vm) {
        vm.cleanEmptyDrafts()
        vm.selectedNoteId = null
        vm.selectedNoteDetail = null
        vm.clearMultiSelection()
    },
    onCompactResize(vm) {
        vm.sidebarCollapsed = true
    },
    onEscape(vm) {
        if (vm.$refs.contextMenu?.visible) {
            vm.$refs.contextMenu.close()
            return true
        }
        if (vm.$refs.editor?.revisionPreview) {
            vm.$refs.editor.revisionPreview = null
            return true
        }
        if (vm.$refs.sidebar?.dismissActiveInput()) return true
        if (vm.selectedNoteId) {
            vm.selectedNoteId = null
            vm.selectedNoteDetail = null
            vm.clearMultiSelection()
            return true
        }
        return false
    },
    extraStorageKeys(vm) {
        return {
            includeShared: vm.includeShared,
        }
    },
    onContext(vm, ctx) {
        if (ctx.itemId) vm.onSelectNote(ctx.itemId)
        if (ctx.newNote) vm.onNewNote()
    },
})

export default {
    name: 'NoteApp',
    mixins: [notePanelMixin],
    components: {NoteSidebar, NoteList, NoteEditor, NoteTrash, NoteContextMenu, PanelFrame},
    data() {
        const saved = this.panelLoadLayout()
        return {
            activeView: 'all',
            selectedCategoryId: null,
            selectedTagId: null,
            selectedColorHex: null,
            includeShared: saved.includeShared || false,
            selectedNoteId: null,
            selectedNoteDetail: null,
            noteLoading: false,
            selectedNoteIds: [],
            lastClickedNoteId: null,
            searchQuery: '',
            sidebarCollapsed: false,
            mobileView: 'list',
            drafts: [],
            draftTrash: [],
            noteUploadProgress: null,

            dividerType: null,
        }
    },
    computed: {
        ownNotes() {
            return this.$store.state.module.note.ownNotes || []
        },
        sharedNotes() {
            return this.$store.state.module.note.sharedNotes || []
        },
        categoryTree() {
            const tree = this.$store.state.module.note.category.tree || []
            if (!this.drafts.length) return tree
            const draftCounts = {}
            for (const d of this.drafts) {
                if (d.category && d.category.id) {
                    draftCounts[d.category.id] = (draftCounts[d.category.id] || 0) + 1
                }
            }
            if (!Object.keys(draftCounts).length) return tree
            const addCounts = (items) => items.map(item => {
                const extra = draftCounts[item.id] || 0
                const children = item.children ? addCounts(item.children) : undefined
                return extra ? {...item, notes_count: (item.notes_count || 0) + extra, children} : (children !== item.children ? {...item, children} : item)
            })
            return addCounts(tree)
        },
        tags() {
            return this.$store.state.module.note.tag.list || []
        },
        trashNotes() {
            const all = [...this.draftTrash, ...(this.$store.state.module.note.trash.list || [])]
            all.sort((a, b) => new Date(b.deleted_at || b.updated_at) - new Date(a.deleted_at || a.updated_at))
            return all
        },
        officers() {
            return this.$handler.getMembers()
        },
        filteredNotes() {
            let notes = []

            if (this.activeView === 'drafts') {
                return [...this.drafts]
            } else if (this.activeView === 'all') {
                notes = this.includeShared ? [...this.ownNotes, ...this.sharedNotes] : [...this.ownNotes]
            } else if (this.activeView === 'shared') {
                notes = [...this.sharedNotes]
            } else if (this.activeView === 'category') {
                notes = this.ownNotes.filter(n => n.category && n.category.id === this.selectedCategoryId)
                if (this.includeShared) {
                    notes = [...notes, ...this.sharedNotes.filter(n => n.category && n.category.id === this.selectedCategoryId)]
                }
            } else if (this.activeView === 'tag') {
                notes = this.ownNotes.filter(n => n.tags && n.tags.some(t => t.id === this.selectedTagId))
                if (this.includeShared) {
                    notes = [...notes, ...this.sharedNotes.filter(n => n.tags && n.tags.some(t => t.id === this.selectedTagId))]
                }
            } else if (this.activeView === 'color') {
                notes = this.ownNotes.filter(n => n.color === this.selectedColorHex)
                if (this.includeShared) {
                    notes = [...notes, ...this.sharedNotes.filter(n => n.color === this.selectedColorHex)]
                }
            }


            if (this.drafts.length) {
                if (this.activeView === 'all') {
                    notes.unshift(...this.drafts)
                } else if (this.activeView === 'category') {
                    notes.unshift(...this.drafts.filter(d => d.category && d.category.id === this.selectedCategoryId))
                } else if (this.activeView === 'tag') {
                    notes.unshift(...this.drafts.filter(d => d.tags && d.tags.some(t => t.id === this.selectedTagId)))
                } else if (this.activeView === 'color') {
                    notes.unshift(...this.drafts.filter(d => d.color === this.selectedColorHex))
                }
            }

            notes.sort((a, b) => {
                if (a._isTemp) return -1
                if (b._isTemp) return 1
                if (a.is_pinned && !b.is_pinned) return -1
                if (!a.is_pinned && b.is_pinned) return 1
                return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at)
            })

            return notes
        },
        hasListContent() {
            if (this.activeView === 'trash') return this.trashNotes.length > 0
            return this.filteredNotes.length > 0
        },
        multiSelectCount() {
            return this.selectedNoteIds.length
        },
        isMultiSelectActive() {
            return this.selectedNoteIds.length > 1
        },
    },
    watch: {},
    created() {
        const watchPath = this.$handler.getContextWatchPath()
        if (watchPath) {
            this._contextUnwatch = this.$watch(watchPath, (val) => {
                if (val && this.visible && this.$handler.getMembers().length === 0) {
                    this.$handler.loadMembers()
                }
            })
        }
    },
    beforeUnmount() {
        this._contextUnwatch?.()
    },
    methods: {
        saveDrafts() {
            localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(this.drafts))
        },
        loadDrafts() {
            const raw = localStorage.getItem(DRAFT_STORAGE_KEY)
            if (!raw) return []
            try {
                const arr = JSON.parse(raw)
                if (Array.isArray(arr)) return arr.filter(d => d && d._isTemp)
            } catch(e) { /* err */ }
            return []
        },
        saveDraftTrash() {
            localStorage.setItem(DRAFT_TRASH_KEY, JSON.stringify(this.draftTrash))
        },
        loadDraftTrash() {
            const raw = localStorage.getItem(DRAFT_TRASH_KEY)
            if (!raw) return []
            try {
                const arr = JSON.parse(raw)
                if (Array.isArray(arr)) return arr
            } catch(e) { /* err */ }
            return []
        },
        getDraft(id) {
            return this.drafts.find(d => d.id === id) || null
        },
        updateDraft(id, updates) {
            const idx = this.drafts.findIndex(d => d.id === id)
            if (idx === -1) return
            const merged = {...this.drafts[idx], ...updates}
            if ('content' in updates) {
                merged.title = generateTitleFromContent(merged.content)
                merged.preview = generatePreview(merged.content, merged.title)
            }
            this.drafts.splice(idx, 1, merged)
            this.saveDrafts()
            if (this.selectedNoteId === id) {
                this.selectedNoteDetail = this.drafts[idx]
            }
        },
        removeDraft(id) {
            const idx = this.drafts.findIndex(d => d.id === id)
            if (idx !== -1) this.drafts.splice(idx, 1)
            this.saveDrafts()
            if (this.selectedNoteId === id) {
                this.selectedNoteId = null
                this.selectedNoteDetail = null
            }
        },
        cleanEmptyDrafts() {
            const emptyIds = this.drafts
                .filter(d => !d.content || d.content === '<p></p>' || d.content === '<p><br></p>')
                .map(d => d.id)
            if (!emptyIds.length) return
            this.drafts = this.drafts.filter(d => !emptyIds.includes(d.id))
            this.saveDrafts()
            if (emptyIds.includes(this.selectedNoteId)) {
                this.selectedNoteId = null
                this.selectedNoteDetail = null
            }
        },

        async loadData() {
            await Promise.all([
                this.$store.dispatch('module/note/get'),
                this.$store.dispatch('module/note/category/getTree'),
                this.$store.dispatch('module/note/tag/get'),
                this.$store.dispatch('module/note/trash/get'),
                this.$handler.loadMembers(),
            ])
            this.drafts = this.loadDrafts()
            this.draftTrash = this.loadDraftTrash()
        },
        async refreshNotes() {
            await this.$store.dispatch('module/note/get', {filterData: this.buildFilterData()})
        },
        async refreshAll() {
            const promises = [
                this.refreshNotes(),
                this.$store.dispatch('module/note/category/getTree'),
                this.$store.dispatch('module/note/tag/get'),
            ]
            if (this.activeView === 'trash') {
                promises.push(this.$store.dispatch('module/note/trash/get'))
            }
            if (this.selectedNoteId && !this.getDraft(this.selectedNoteId) && this.activeView !== 'trash') {
                promises.push(
                    this.$store.dispatch('module/note/show', {id: this.selectedNoteId}).then(data => {
                        if (data) this.selectedNoteDetail = data
                    })
                )
            }
            await Promise.all(promises)
        },
        buildFilterData() {
            const data = {}
            if (this.searchQuery) data.search = this.searchQuery
            return Object.keys(data).length ? data : undefined
        },

        clearMultiSelection() {
            this.selectedNoteIds = []
            this.lastClickedNoteId = null
        },
        clearSelection() {
            this.selectedNoteId = null
            this.selectedNoteDetail = null
            this.clearMultiSelection()
            if (this.$refs.editor) this.$refs.editor.showSettings = false
        },
        onSelectView(view) {
            this.cleanEmptyDrafts()
            this.activeView = view
            this.selectedCategoryId = null
            this.selectedTagId = null
            this.selectedColorHex = null
            this.clearSelection()
            if (view === 'trash') {
                this.$store.dispatch('module/note/trash/get')
            }
            if (this.isCompact) { this.sidebarCollapsed = true; this.mobileView = 'list' }
        },
        onSelectCategory(cat) {
            this.cleanEmptyDrafts()
            this.activeView = 'category'
            this.selectedCategoryId = cat.id
            this.selectedTagId = null
            this.selectedColorHex = null
            this.clearSelection()
            if (this.isCompact) { this.sidebarCollapsed = true; this.mobileView = 'list' }
        },
        onSelectTag(tag) {
            this.cleanEmptyDrafts()
            this.activeView = 'tag'
            this.selectedTagId = tag.id
            this.selectedCategoryId = null
            this.selectedColorHex = null
            this.clearSelection()
            if (this.isCompact) { this.sidebarCollapsed = true; this.mobileView = 'list' }
        },
        onSelectColor(hex) {
            this.cleanEmptyDrafts()
            if (hex) {
                this.activeView = 'color'
                this.selectedColorHex = hex
                this.selectedCategoryId = null
                this.selectedTagId = null
            } else {
                this.activeView = 'all'
                this.selectedColorHex = null
            }
            this.clearSelection()
            if (this.isCompact) { this.sidebarCollapsed = true; this.mobileView = 'list' }
        },
        onToggleIncludeShared(val) {
            this.includeShared = val
            this.panelSaveLayoutDebounced()
        },
        async onAddCategory(formData) {
            await this.$store.dispatch('module/note/category/add', {formData})
            await this.$store.dispatch('module/note/category/getTree')
        },
        async onAddTag(formData) {
            await this.$store.dispatch('module/note/tag/add', {formData})
            await this.$store.dispatch('module/note/tag/get')
        },
        async onRenameCategory({id, name}) {
            await this.$store.dispatch('module/note/category/update', {id, formData: {name}})
            await this.$store.dispatch('module/note/category/getTree')
        },
        async onNoteDrop({noteId, categoryId}) {
            if (this.selectedNoteIds.length > 1 && this.selectedNoteIds.includes(noteId)) {
                await this.onBatchMove(categoryId)
                return
            }
            const draft = this.getDraft(noteId)
            if (draft) {
                await this.createDraftWithCategory(draft, categoryId)
                return
            }
            const sourceCategoryId = this.activeView === 'category' ? this.selectedCategoryId : null
            await this.$store.dispatch('module/note/moveToCategory', {id: noteId, category_id: categoryId})
            await this.refreshAll()
            if (sourceCategoryId && this.activeView === 'category' && this.selectedCategoryId === sourceCategoryId) {
                if (this.filteredNotes.length === 0 && categoryId) {
                    this.selectedCategoryId = categoryId
                }
            }
        },
        async onNoteDropTag({noteId, tagId}) {
            const draft = this.getDraft(noteId)
            if (draft) {
                const currentTags = draft.tags || []
                if (currentTags.some(t => t.id === tagId)) return
                const tag = this.tags.find(t => t.id === tagId)
                if (!tag) return
                this.updateDraft(noteId, {tags: [...currentTags, {id: tag.id, name: tag.name}]})
                return
            }
            const note = this.ownNotes.find(n => n.id === noteId)
            if (!note) return
            const currentTagIds = (note.tags || []).map(t => t.id)
            if (currentTagIds.includes(tagId)) return
            const data = await this.$store.dispatch('module/note/show', {id: noteId})
            if (!data) return
            const formData = {
                content: data.content || '',
                color: data.color || null,
                category_id: data.category?.id || null,
                tag_ids: [...(data.tags || []).map(t => t.id), tagId],
                visibility_id: data.visibility?.id || 1,
                edit_permission_id: data.edit_permission?.id || 1,
                viewer_ids: (data.viewers || []).map(v => v.id),
                editor_ids: (data.editors || []).map(e => e.id),
            }
            await this.$store.dispatch('module/note/update', {id: noteId, formData})
            await this.refreshAll()
            if (this.selectedNoteId === noteId) {
                const updated = await this.$store.dispatch('module/note/show', {id: noteId})
                if (updated) this.selectedNoteDetail = updated
            }
        },
        async onCategoryDropTrash({categoryId}) {
            await this.$store.dispatch('module/note/category/delete', {id: categoryId})
            await this.$store.dispatch('module/note/category/getTree')
            if (this.selectedCategoryId === categoryId) {
                this.activeView = 'all'
                this.selectedCategoryId = null
            }
            await this.refreshNotes()
        },
        async onTagDropTrash({tagId}) {
            await this.$store.dispatch('module/note/tag/delete', {id: tagId})
            await this.$store.dispatch('module/note/tag/get')
            if (this.selectedTagId === tagId) {
                this.activeView = 'all'
                this.selectedTagId = null
            }
        },
        async onNoteDropTrash({noteId}) {
            if (this.selectedNoteIds.length > 1 && this.selectedNoteIds.includes(noteId)) {
                await this.onBatchDelete()
                return
            }
            const note = this.getDraft(noteId) || this.ownNotes.find(n => n.id === noteId)
            if (note) await this.onDeleteNote(note)
        },
        async onTouchDragTrash({noteId}) {
            if (this.$refs.sidebar) {
                this.$refs.sidebar.trashEating = true
            }
            await new Promise(r => setTimeout(r, 600))
            if (this.$refs.sidebar) {
                this.$refs.sidebar.trashEating = false
            }
            await this.onNoteDropTrash({noteId})
        },
        onTagContextMenu(event, tag) {
            this.$refs.contextMenu.openForTag(event, tag)
        },
        async onCategoryDrop({categoryId, parentId}) {
            await this.$store.dispatch('module/note/category/update', {id: categoryId, formData: {parent_id: parentId}})
            await this.$store.dispatch('module/note/category/getTree')
        },
        async onCategoryReorder(updates) {
            const items = updates.map(u => {
                const item = {id: u.id, sort: u.sort}
                if (u.parentChanged) item.parent_id = u.parent_id
                return item
            })
            await this.$store.dispatch('module/note/category/batchUpdate', {items})
            await this.$store.dispatch('module/note/category/getTree')
        },
        async onContextDeleteTag(tag) {
            await this.$store.dispatch('module/note/tag/delete', {id: tag.id})
            await this.$store.dispatch('module/note/tag/get')
            if (this.selectedTagId === tag.id) {
                this.activeView = 'all'
                this.selectedTagId = null
            }
        },

        findCategoryById(id) {
            const search = (items) => {
                for (const item of items) {
                    if (item.id === id) return item
                    if (item.children) {
                        const found = search(item.children)
                        if (found) return found
                    }
                }
                return null
            }
            return search(this.categoryTree)
        },
        async createTempOnServer(draftId, formData) {
            const res = await this.$store.dispatch('module/note/add', {formData})
            if (res && res.data) {
                this.removeDraft(draftId)
                this.selectedNoteId = res.data.id
                await this.refreshNotes()
                await this.$store.dispatch('module/note/category/getTree')
                const data = await this.$store.dispatch('module/note/show', {id: res.data.id})
                if (data) this.selectedNoteDetail = data
                return res.data.id
            }
            return null
        },
        async createDraftWithCategory(draft, categoryId) {
            const formData = {
                content: draft.content || '',
                color: draft.color || null,
                category_id: categoryId || null,
                visibility_id: draft.visibility?.id || 1,
                edit_permission_id: draft.edit_permission?.id || 1,
                viewer_ids: draft.viewer_ids || [],
                editor_ids: draft.editor_ids || [],
                tag_ids: draft.tag_ids || [],
            }
            await this.createTempOnServer(draft.id, formData)
            await this.$store.dispatch('module/note/category/getTree')
        },
        async onSelectNote(id) {
            this.clearMultiSelection()
            if (this.selectedNoteId === id && !this.isCompact) return
            this.cleanEmptyDrafts()
            if (this.$refs.editor) this.$refs.editor.showSettings = false
            this.selectedNoteId = id
            this.selectedNoteDetail = null
            if (this.isCompact) this.mobileView = 'editor'
            const draft = this.getDraft(id)
            if (draft) {
                this.selectedNoteDetail = draft
                return
            }
            this.noteLoading = true
            try {
                const data = await this.$store.dispatch('module/note/show', {id})
                if (data && this.selectedNoteId === id) this.selectedNoteDetail = data
            } finally {
                this.noteLoading = false
            }
        },
        onNewNote() {
            this.cleanEmptyDrafts()
            const category = (this.activeView === 'category' && this.selectedCategoryId)
                ? this.findCategoryById(this.selectedCategoryId)
                : null
            const now = new Date().toISOString()
            const newDraft = {
                id: 'temp_' + Date.now(),
                title: '',
                content: '<p></p>',
                preview: null,
                color: null,
                category: category ? {id: category.id, name: category.name, color: category.color} : null,
                tags: [],
                is_owner: true,
                can_edit: true,
                is_pinned: false,
                has_agenda: false,
                visibility: {id: 1},
                edit_permission: {id: 1},
                viewers: [],
                editors: [],
                revisions: [],
                created_at: now,
                updated_at: now,
                _isTemp: true,
            }
            this.drafts.push(newDraft)
            this.saveDrafts()
            this.selectedNoteId = newDraft.id
            this.selectedNoteDetail = newDraft
            if (this.isCompact) this.mobileView = 'editor'
        },
        onSearch(query) {
            this.searchQuery = query
            this.refreshNotes()
        },

        async onColorChange({id, color}) {
            if (this.getDraft(id)) {
                this.updateDraft(id, {color})
                return
            }
            await this.$store.dispatch('module/note/updateColor', {id, color})
            await this.refreshNotes()
        },
        async onSaveNote({id, formData, pendingUploads}) {
            if (this.getDraft(id)) {
                if (this._creatingTemp) return
                this._creatingTemp = true
                try {
                    await this.createTempOnServer(id, formData)
                } finally {
                    this._creatingTemp = false
                }
                return
            }

            if (pendingUploads && pendingUploads.length > 0) {
                try {
                    formData.content = await this.uploadPendingImages(id, formData.content, pendingUploads)
                } catch (e) {
                    this.noteUploadProgress = null
                    if (e.cancelled) return
                    this.showUploadError(e)
                    return
                }
            }

            await this.$store.dispatch('module/note/update', {id, formData})
            await this.refreshAll()

            const data = await this.$store.dispatch('module/note/show', {id})
            if (data) this.selectedNoteDetail = data
        },
        async onDeleteNote(note) {
            const draft = this.getDraft(note.id)
            if (draft) {
                const isEmpty = !draft.content || draft.content === '<p></p>' || draft.content === '<p><br></p>'
                if (!isEmpty) {
                    this.draftTrash.push({...draft, deleted_at: new Date().toISOString()})
                    this.saveDraftTrash()
                }
                this.removeDraft(note.id)
                return
            }
            await this.$store.dispatch('module/note/delete', {id: note.id})
            this.selectedNoteId = null
            this.selectedNoteDetail = null
            await this.refreshAll()
            await this.$store.dispatch('module/note/trash/get')
        },
        async onTogglePin(note) {
            const draft = this.getDraft(note.id)
            if (draft) {
                this.updateDraft(note.id, {is_pinned: !draft.is_pinned})
                return
            }
            if (note.is_pinned) {
                await this.$store.dispatch('module/note/unpin', {noteId: note.id})
            } else {
                await this.$store.dispatch('module/note/pin', {noteId: note.id})
            }
            await this.refreshNotes()
            if (this.selectedNoteId === note.id) {
                const data = await this.$store.dispatch('module/note/show', {id: note.id})
                if (data) this.selectedNoteDetail = data
            }
        },

        async onReminderSave({id, event_at, reminder_at}) {
            if (this.getDraft(id)) {
                this.updateDraft(id, {agenda: {event_at, reminder_at}, has_agenda: true})
                return
            }
            await this.$store.dispatch('module/note/setReminder', {noteId: id, event_at, reminder_at})
            await this.refreshNotes()
            const data = await this.$store.dispatch('module/note/show', {id})
            if (data) this.selectedNoteDetail = data
                    },
        async onReminderDelete({id}) {
            if (this.getDraft(id)) {
                this.updateDraft(id, {agenda: null, has_agenda: false})
                return
            }
            await this.$store.dispatch('module/note/deleteReminder', {noteId: id})
            await this.refreshNotes()
            const data = await this.$store.dispatch('module/note/show', {id})
            if (data) this.selectedNoteDetail = data
        },

        async uploadPendingImages(noteId, content, pendingUploads) {
            this._uploadAbortController = new AbortController()
            let finalContent = content
            const total = pendingUploads.length

            this.noteUploadProgress = { total, completed: 0, percent: 0, speed: '', currentFileName: '' }

            for (let i = 0; i < total; i++) {
                const {blobUrl, file} = pendingUploads[i]
                this.noteUploadProgress.currentFileName = file.name

                const fd = new FormData()
                fd.append('files[]', file)
                fd.append('uploadable_type', 'MODULE_NOTE')
                fd.append('uploadable_id', noteId)

                const startTime = Date.now()
                let res
                try {
                    res = await this.axios.post(
                        this.endpoints['upload'],
                        fd,
                        {
                            signal: this._uploadAbortController.signal,
                            onUploadProgress: (e) => {
                                if (e.lengthComputable) {
                                    const filePercent = (e.loaded / e.total) * 100
                                    const overallPercent = ((i + filePercent / 100) / total) * 100
                                    const elapsed = (Date.now() - startTime) / 1000
                                    const speed = elapsed > 0 ? this.formatSpeed(e.loaded / elapsed) : ''
                                    this.noteUploadProgress.percent = Math.round(overallPercent)
                                    this.noteUploadProgress.speed = speed
                                }
                            }
                        }
                    )
                } catch (e) {
                    if (e.name === 'AbortError' || e.name === 'CanceledError' || this._uploadAbortController.signal.aborted) {
                        const err = new Error('Upload cancelled')
                        err.cancelled = true
                        throw err
                    }
                    throw e
                }

                const serverUrl = res.data.data[0].public_url
                finalContent = finalContent.replaceAll(blobUrl, serverUrl)
                URL.revokeObjectURL(blobUrl)
                this.noteUploadProgress.completed = i + 1
            }

            this.noteUploadProgress = null
            this.$refs.editor?.$refs?.ckeditor?.clearPendingUploads()
            return finalContent
        },
        onCancelUpload() {
            if (this._uploadAbortController) {
                this._uploadAbortController.abort()
            }
            this.noteUploadProgress = null
        },
        showUploadError(e) {
            const msg = e?.response?.data?.message
            const map = {
                storage_quota_not_found: this.$t('common.storage.quotaNotFound'),
                storage_quota_exceeded: this.$t('common.storage.quotaExceeded'),
            }
            this.eventBus.$emit('showConfirmDialog', {
                title: this.$t('pages.module.NOTE.editor.uploadErrorTitle'),
                message: map[msg] || this.$t('pages.module.NOTE.editor.uploadErrorGeneric'),
                confirmText: this.$t('common.ok'),
                confirmType: 'default',
                singleButton: true,
            })
        },
        formatSpeed(bytesPerSec) {
            if (bytesPerSec > 1048576) return (bytesPerSec / 1048576).toFixed(1) + ' MB/s'
            if (bytesPerSec > 1024) return (bytesPerSec / 1024).toFixed(0) + ' KB/s'
            return bytesPerSec.toFixed(0) + ' B/s'
        },

        onDraftUpdate({id, formData}) {
            if (this.getDraft(id)) {
                this.updateDraft(id, {
                    content: formData.content,
                    color: formData.color,
                    category_id: formData.category_id,
                    tag_ids: formData.tag_ids,
                    visibility: {id: formData.visibility_id},
                    edit_permission: {id: formData.edit_permission_id},
                    viewer_ids: formData.viewer_ids,
                    editor_ids: formData.editor_ids,
                })
            }
        },

        async onTrashSelectNote(note) {
            if (this.selectedNoteId === note.id && !this.isCompact) return
            this.selectedNoteId = note.id
            this.selectedNoteDetail = null
            if (this.isCompact) this.mobileView = 'editor'
            if (note._isTemp) {
                this.selectedNoteDetail = {...note, can_edit: false, _isTrashed: true}
                return
            }
            this.noteLoading = true
            try {
                const data = await this.$store.dispatch('module/note/show', {id: note.id, with_trashed: true})
                if (data) {
                    this.selectedNoteDetail = {...data, can_edit: false, _isTrashed: true}
                } else {
                    this.selectedNoteId = null
                    this.selectedNoteDetail = null
                    await this.$store.dispatch('module/note/trash/get')
                }
            } finally {
                this.noteLoading = false
            }
        },
        async onTrashRestore({id, restoreCategory}) {
            this.selectedNoteId = null
            this.selectedNoteDetail = null
            const draftIdx = this.draftTrash.findIndex(d => d.id === id)
            if (draftIdx !== -1) {
                const restored = {...this.draftTrash[draftIdx]}
                delete restored.deleted_at
                this.draftTrash.splice(draftIdx, 1)
                this.saveDraftTrash()
                this.drafts.push(restored)
                this.saveDrafts()
                return
            }
            await this.$store.dispatch('module/note/trash/restore', {id, restoreCategory})
            await this.$store.dispatch('module/note/trash/get')
            await this.refreshAll()
        },
        async onTrashPermanentDelete({id}) {
            if (this.selectedNoteId === id) {
                this.selectedNoteId = null
                this.selectedNoteDetail = null
            }
            const draftIdx = this.draftTrash.findIndex(d => d.id === id)
            if (draftIdx !== -1) {
                this.draftTrash.splice(draftIdx, 1)
                this.saveDraftTrash()
                return
            }
            await this.$store.dispatch('module/note/trash/permanentDelete', {id})
            await this.$store.dispatch('module/note/trash/get')
        },

        onNoteContextMenu(event, note) {
            if (!note.is_owner) return
            this.$refs.contextMenu.openForNote(event, note)
        },
        onCategoryContextMenu(event, category) {
            this.$refs.contextMenu.openForCategory(event, category)
        },
        async onContextPin(note) {
            await this.onTogglePin(note)
        },
        async onContextMove({note, categoryId}) {
            const draft = this.getDraft(note.id)
            if (draft) {
                await this.createDraftWithCategory(draft, categoryId)
                return
            }
            const fromCategoryId = note.category?.id
            await this.$store.dispatch('module/note/moveToCategory', {id: note.id, category_id: categoryId})
            await this.refreshAll()
            if (this.activeView === 'category' && this.selectedCategoryId === fromCategoryId) {
                if (this.filteredNotes.length === 0 && categoryId) {
                    this.selectedCategoryId = categoryId
                }
            }
        },
        async onContextDuplicate(note) {
            const data = await this.$store.dispatch('module/note/show', {id: note.id})
            if (data) {
                const res = await this.$store.dispatch('module/note/add', {
                    formData: {
                        content: data.content,
                        color: data.color,
                        category_id: data.category?.id || null,
                        tag_ids: (data.tags || []).map(t => t.id),
                    }
                })
                await this.refreshNotes()
                if (res && res.data) {
                    this.onSelectNote(res.data.id)
                }
            }
        },
        onContextShare(note) {
            this.onSelectNote(note.id)
            this.$nextTick(() => {
                this.$refs.editor?.scrollToSettings()
            })
        },
        async onContextDelete(note) {
            await this.onDeleteNote(note)
        },
        onContextRenameCategory(category) {
            this.$refs.sidebar?.startRename(category)
        },
        onContextNewSubCategory(category) {
            this.$refs.sidebar?.startNewSubCategory(category)
        },
        async onContextCategoryColor({category, color}) {
            await this.$store.dispatch('module/note/category/update', {
                id: category.id,
                formData: {color}
            })
            await this.$store.dispatch('module/note/category/getTree')
        },
        async onContextDeleteCategory(category) {
            await this.$store.dispatch('module/note/category/delete', {id: category.id})
            await this.$store.dispatch('module/note/category/getTree')
            if (this.selectedCategoryId === category.id) {
                this.activeView = 'all'
                this.selectedCategoryId = null
            }
            await this.refreshNotes()
        },

        onMultiSelectNote({noteId, isCtrlOrCmd, isShift, note}) {
            if (note.is_owner === false) return
            if (isCtrlOrCmd) {
                this.handleCtrlClick(noteId)
            } else if (isShift) {
                this.handleShiftClick(noteId)
            }
        },
        handleCtrlClick(noteId) {
            if (this.selectedNoteIds.length === 0 && this.selectedNoteId != null) {
                const currentNote = this.filteredNotes.find(n => n.id === this.selectedNoteId)
                if (currentNote && currentNote.is_owner !== false) {
                    this.selectedNoteIds.push(this.selectedNoteId)
                }
            }
            const idx = this.selectedNoteIds.indexOf(noteId)
            if (idx !== -1) {
                this.selectedNoteIds.splice(idx, 1)
            } else {
                this.selectedNoteIds.push(noteId)
            }
            this.lastClickedNoteId = noteId
            this.selectedNoteDetail = null
        },
        handleShiftClick(noteId) {
            const notes = this.filteredNotes
            const anchorId = this.lastClickedNoteId || this.selectedNoteId
            let anchorIdx = anchorId != null ? notes.findIndex(n => n.id === anchorId) : -1
            if (anchorIdx === -1) anchorIdx = 0
            const targetIdx = notes.findIndex(n => n.id === noteId)
            if (targetIdx === -1) return
            const start = Math.min(anchorIdx, targetIdx)
            const end = Math.max(anchorIdx, targetIdx)
            const rangeIds = []
            for (let i = start; i <= end; i++) {
                if (notes[i].is_owner !== false) {
                    rangeIds.push(notes[i].id)
                }
            }
            this.selectedNoteIds = rangeIds
            this.selectedNoteDetail = null
        },
        async onBatchDelete() {
            const draftIds = this.selectedNoteIds.filter(id => this.getDraft(id))
            const serverIds = this.selectedNoteIds.filter(id => !this.getDraft(id))

            for (const id of draftIds) {
                const draft = this.getDraft(id)
                if (draft) {
                    const isEmpty = !draft.content || draft.content === '<p></p>' || draft.content === '<p><br></p>'
                    if (!isEmpty) {
                        this.draftTrash.push({...draft, deleted_at: new Date().toISOString()})
                    }
                    this.removeDraft(id)
                }
            }
            if (draftIds.length) this.saveDraftTrash()

            if (serverIds.length) {
                await this.$store.dispatch('module/note/batchDelete', {ids: serverIds})
            }

            this.clearMultiSelection()
            this.selectedNoteId = null
            this.selectedNoteDetail = null
            await this.refreshAll()
            await this.$store.dispatch('module/note/trash/get')
        },
        async onBatchMove(categoryId) {
            const draftIds = this.selectedNoteIds.filter(id => this.getDraft(id))
            const serverIds = this.selectedNoteIds.filter(id => !this.getDraft(id))

            for (const id of draftIds) {
                const draft = this.getDraft(id)
                if (draft) {
                    await this.createDraftWithCategory(draft, categoryId)
                }
            }

            if (serverIds.length) {
                await this.$store.dispatch('module/note/batchMove', {ids: serverIds, category_id: categoryId})
            }

            this.clearMultiSelection()
            this.selectedNoteId = null
            this.selectedNoteDetail = null
            await this.refreshAll()
        },
    },
}
</script>

<style>
@import '../panel/panel-base.css';

.note-app {
    --note-sidebar-bg: #f2f2f7;
    --note-list-bg: #ffffff;
    --note-selected-sidebar: rgba(var(--panel-accent-rgb), 0.1);
    --note-group-header: #8e8e93;
    --note-item-hover: #f5f5f5;
}

html.dark .note-app {
    --note-sidebar-bg: #1B1C22;
    --note-list-bg: #15171C;
    --note-selected-sidebar: rgba(var(--panel-accent-rgb), 0.12);
    --note-group-header: #636674;
    --note-item-hover: #1E2027;
    --note-skeleton: rgba(255, 255, 255, 0.08);
    --note-skeleton-strong: rgba(255, 255, 255, 0.13);
}

html.midnight .note-app {
    --note-sidebar-bg: #201D19;
    --note-list-bg: #1A1714;
    --note-selected-sidebar: rgba(var(--panel-accent-rgb), 0.12);
    --note-group-header: #6B655E;
    --note-item-hover: #241F1B;
    --note-skeleton: rgba(255, 255, 255, 0.07);
    --note-skeleton-strong: rgba(255, 255, 255, 0.12);
}

html.cream .note-app {
    --note-sidebar-bg: #F5F1EA;
    --note-list-bg: #FEFCF8;
    --note-selected-sidebar: rgba(var(--panel-accent-rgb), 0.08);
    --note-group-header: #A39B8F;
    --note-item-hover: #F0EBE3;
}

.note-app-titlebar-text {
    font-size: 13px;
    font-weight: 600;
    color: var(--panel-text-primary);
}

.note-app-sidebar {
    flex-shrink: 0;
    border-right: 1px solid var(--panel-border);
    overflow: hidden;
}

.note-app-list {
    flex-shrink: 0;
    border-right: 1px solid var(--panel-border);
    overflow: hidden;
}

.note-app-editor {
    flex: 1;
    min-width: 0;
    overflow: hidden;
}


.note-app-popper.el-popper {
    z-index: 200010 !important;
}




.note-app-editor .el-select--small .el-input__wrapper,
.note-app-editor .el-select--small .el-select__wrapper {
    min-height: 28px;
}


.note-color-popper.el-popper {
    padding: 8px !important;
}

.note-color-popper .color-popover-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 6px;
}

.note-color-popper .color-popover-item {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.15s, box-shadow 0.15s;
    border: 2px solid transparent;
}

.note-color-popper .color-popover-item:hover {
    transform: scale(1.12);
}

.note-color-popper .color-popover-item.active {
    border-color: var(--panel-text-primary, #1c1c1e);
    box-shadow: 0 0 0 2px var(--panel-bg, #ffffff), 0 0 0 4px var(--panel-text-primary, #1c1c1e);
}

.note-color-popper .color-popover-item--none {
    background: var(--panel-border, #e5e5ea);
    color: var(--panel-text-secondary, #8e8e93);
}

.note-reminder-popper.el-popper {
    padding: 12px !important;
}

.note-reminder-popper .reminder-popover {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.note-reminder-popper .reminder-popover-title {
    color: #1c1c1e;
    font-size: 13px;
    font-weight: 600;
}

.note-reminder-popper .reminder-popover-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.note-reminder-popper .reminder-popover-field label {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #8e8e93;
}

.note-reminder-popper .el-date-editor .el-input__wrapper {
    min-height: 28px;
}

.note-reminder-popper .reminder-popover-actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 2px;
}

html.dark .note-reminder-popper.el-popper {
    background: #1E2027;
    border-color: #26272F;
}

html.dark .note-reminder-popper .reminder-popover-title {
    color: #F5F5F5;
}

html.dark .note-reminder-popper .reminder-popover-field label {
    color: #9A9CAE;
}

html.dark .note-reminder-popper .el-popper__arrow::before {
    background: #1E2027;
    border-color: #26272F;
}

html.midnight .note-reminder-popper.el-popper {
    background: #241F1B;
    border-color: #2C2823;
}

html.midnight .note-reminder-popper .reminder-popover-title {
    color: #EAE6E1;
}

html.midnight .note-reminder-popper .reminder-popover-field label {
    color: #A9A39D;
}

html.midnight .note-reminder-popper .el-popper__arrow::before {
    background: #241F1B;
    border-color: #2C2823;
}


.note-app.panel-app--compact .note-app-sidebar {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100% !important;
    z-index: 10;
    border-right: none;
    background: var(--note-sidebar-bg, #f2f2f7);
}

.note-app.panel-app--compact .note-app-sidebar > * {
    height: 100%;
}

.note-app.panel-app--compact .note-app-list {
    width: 100% !important;
    flex: 1;
    border-right: none;
}

.note-app.panel-app--compact .note-app-editor {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.note-app-mobile-back {
    display: flex;
    align-items: center;
    padding: 6px 10px;
    border-bottom: 1px solid var(--panel-border, #e5e5ea);
    flex-shrink: 0;
    background: var(--panel-bg, #ffffff);
}

.mobile-back-btn {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    color: var(--panel-accent-text);
    font-weight: 500;
}

.mobile-back-btn:hover {
    background: var(--panel-selected-bg);
}

.mobile-back-actions {
    display: flex;
    align-items: center;
    gap: 2px;
    margin-left: auto;
}

.mobile-back-actions .toolbar-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    color: var(--panel-text-secondary, #8e8e93);
    transition: background 0.15s, color 0.15s;
}

.mobile-back-actions .toolbar-icon-btn:hover {
    background: var(--panel-selected-bg);
    color: var(--panel-text-primary);
}

.mobile-back-actions .toolbar-icon-btn--pinned {
    color: var(--panel-accent-text);
}

.mobile-back-actions .toolbar-icon-btn--reminder-active {
    color: var(--panel-accent-text);
}

.mobile-back-actions .toolbar-icon-btn--active {
    background: var(--panel-selected-bg);
    color: var(--panel-accent-text);
}

.mobile-back-actions .toolbar-icon-btn--delete:hover {
    background: rgba(255, 59, 48, 0.08);
    color: #FF3B30;
}

.mobile-back-actions .reminder-icon-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.mobile-back-actions .reminder-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--panel-accent);
}


.note-multi-select-panel {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    gap: 16px;
    padding: 40px 20px;
    text-align: center;
}

.note-multi-select-info {
    display: flex;
    align-items: center;
    gap: 8px;
}

.note-multi-select-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 28px;
    height: 28px;
    padding: 0 8px;
    background: var(--panel-accent);
    color: #fff;
    border-radius: 14px;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
}

.note-multi-select-label {
    font-size: 15px;
    font-weight: 500;
    color: var(--panel-text-primary, #1c1c1e);
}

.note-multi-select-actions {
    display: flex;
    gap: 8px;
}

.note-multi-select-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 16px;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s, color 0.15s;
}

.note-multi-select-btn--delete {
    background: rgba(255, 59, 48, 0.1);
    color: #FF3B30;
}

.note-multi-select-btn--delete:hover {
    background: rgba(255, 59, 48, 0.2);
}

.note-multi-select-hint {
    font-size: 12px;
    color: var(--panel-text-secondary, #8e8e93);
}


.note-app .panel-body {
    position: relative;
}

.note-upload-overlay {
    position: absolute;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
}

.note-upload-overlay-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 32px 40px;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    max-width: 320px;
    width: 100%;
}

.note-upload-overlay-spinner {
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255, 255, 255, 0.25);
    border-top-color: var(--panel-accent);
    border-radius: 50%;
    animation: noteUploadSpinnerRotate 0.8s linear infinite;
}

@keyframes noteUploadSpinnerRotate {
    to { transform: rotate(360deg); }
}

.note-upload-overlay-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--panel-text-primary, #1c1c1e);
}

.note-upload-overlay-stats {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
    color: var(--panel-text-secondary, #8e8e93);
}

.note-upload-overlay-bar {
    width: 100%;
    height: 4px;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 2px;
    overflow: hidden;
}

.note-upload-overlay-bar-fill {
    height: 100%;
    background: var(--panel-accent);
    border-radius: 2px;
    transition: width 0.2s ease;
}

.note-upload-overlay-filename {
    font-size: 11px;
    color: var(--panel-text-secondary, #8e8e93);
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.note-upload-overlay-cancel {
    margin-top: 4px;
    padding: 7px 22px;
    background: rgba(255, 59, 48, 0.08);
    color: #FF3B30;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    font-family: inherit;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;
}

.note-upload-overlay-cancel:hover {
    background: #FF3B30;
    color: #fff;
}

html.dark .note-upload-overlay-content {
    background: rgba(21, 23, 28, 0.92);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

html.dark .note-upload-overlay-cancel {
    background: rgba(255, 69, 58, 0.12);
    color: #FF453A;
}

html.dark .note-upload-overlay-cancel:hover {
    background: #FF453A;
    color: #fff;
}

html.midnight .note-upload-overlay-content {
    background: rgba(26, 23, 20, 0.92);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

html.midnight .note-upload-overlay-cancel {
    background: rgba(255, 69, 58, 0.1);
    color: #FF453A;
}

html.midnight .note-upload-overlay-cancel:hover {
    background: #FF453A;
    color: #fff;
}


</style>
